import { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatCurrency } from '../../lib/utils';
import { 
  LineChart, 
  TrendingUp, 
  TrendingDown,
  ArrowUpRight, 
  Activity,
  Zap,
  FileText,
  Download,
  Send,
  Loader2,
  X,
  Info
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

// Quarterly forecast windows
const QUARTERS = [
  { value: 'Q1', label: 'Q1', days: '90', description: 'Next 3 months', range: '0-3 months' },
  { value: 'Q2', label: 'Q2', days: '6mo', description: 'Months 3-6', range: '3-6 months' },
  { value: 'Q3', label: 'Q3', days: '9mo', description: 'Months 6-9', range: '6-9 months' },
  { value: 'Q4', label: 'Q4', days: '12mo', description: 'Months 9-12', range: '9-12 months' },
];

export function PipelineMomentum() {
  const { get, post } = useApi();
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('executive');
  const [generating, setGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', message: '' });
  const [clickedPoint, setClickedPoint] = useState(null);

  // Map quarter to API timeframe
  const getTimeframeForQuarter = (quarter) => {
    const q = QUARTERS.find(q => q.value === quarter);
    return q ? q.days : '90';
  };

  useEffect(() => {
    fetchIntelligence();
  }, [selectedQuarter]);

  const fetchIntelligence = async () => {
    setLoading(true);
    try {
      const timeframe = getTimeframeForQuarter(selectedQuarter);
      const data = await get(`/pipeline/intelligence?timeframe=${timeframe}`);
      setIntelligence(data);
    } catch (error) {
      console.error('Failed to fetch pipeline intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const result = await post(`/pipeline/generate-report?report_type=${selectedReportType}`);
      setGeneratedReport(result);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to || !generatedReport) return;
    
    setSendingEmail(true);
    try {
      await post('/gmail/send', {
        to: emailForm.to,
        subject: selectedReportType === 'executive' 
          ? 'Pipeline Intelligence Report' 
          : 'Vision Progress Report',
        body: emailForm.message || 'Please find attached the pipeline report.',
        attachments: [{
          id: generatedReport.report_id || `report-${Date.now()}`,
          name: generatedReport.filename,
          url: generatedReport.download_url,
          type: 'application/pdf'
        }]
      });
      toast.success('Report sent successfully');
      setSendEmailOpen(false);
      setEmailForm({ to: '', message: '' });
    } catch (error) {
      // Surface the actual backend reason instead of a generic failure toast.
      // Common path: Gmail OAuth isn't connected → backend returns
      // 400 {detail: "Gmail not connected"}.
      const detail = error?.response?.data?.detail;
      const reason = typeof detail === 'string'
        ? detail
        : (detail?.message || error?.message || 'Failed to send email');
      if (typeof reason === 'string' && reason.toLowerCase().includes('gmail not connected')) {
        toast.error('Gmail not connected', {
          description: 'Connect your Gmail account in Settings → Email Integration, then try again.',
          duration: 8000,
        });
      } else {
        toast.error(reason);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const point = data.activePayload[0].payload;
      setClickedPoint(point);
    }
  };

  const getMomentumColor = (status) => {
    switch (status) {
      case 'strong': return 'text-emerald-600';
      case 'stable': return 'text-amber-600';
      case 'needs_attention': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getMomentumBg = (status) => {
    switch (status) {
      case 'strong': return 'bg-emerald-50 border-emerald-200';
      case 'stable': return 'bg-amber-50 border-amber-200';
      case 'needs_attention': return 'bg-red-50 border-red-200';
      default: return 'bg-muted/30 border-muted';
    }
  };

  const getMomentumLabel = (status) => {
    switch (status) {
      case 'strong': return 'Strong Momentum';
      case 'stable': return 'Stable Momentum';
      case 'needs_attention': return 'Needs Attention';
      default: return 'Unknown';
    }
  };

  if (loading && !intelligence) {
    return (
      <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-lg">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#B89B5E]" />
        </CardContent>
      </Card>
    );
  }

  const summary = intelligence?.summary || {};
  const trendData = intelligence?.trend_data || [];

  return (
    <>
      <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-lg overflow-hidden" data-testid="pipeline-momentum-section">
        <CardHeader className="border-b border-[#E8E6E1] bg-gradient-to-r from-[#FFFEF5] to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg sm:text-xl font-light text-[#1A1A1A] flex items-center gap-2">
                <LineChart className="w-5 h-5 text-[#B89B5E]" />
                Pipeline Momentum
              </CardTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Strategic intelligence and forward revenue outlook
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
              {/* Generate Report Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReportModalOpen(true)}
                className="border-[#B89B5E]/30 hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 text-[#B89B5E]"
                data-testid="generate-report-btn"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                Generate Report
              </Button>
              
              {/* Quarterly Forecast Selector */}
              <div className="flex items-center gap-3">
                <TooltipProvider delayDuration={100}>
                  <div className="inline-flex items-center bg-[#F5F4F0] rounded-xl p-1.5 shadow-inner">
                    {QUARTERS.map((quarter, index) => (
                      <Tooltip key={quarter.value} delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setSelectedQuarter(quarter.value)}
                            className={`
                              relative px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                              min-w-[52px] flex items-center justify-center
                              ${selectedQuarter === quarter.value 
                                ? 'bg-[#B89B5E] text-white shadow-md' 
                                : 'text-[#6B7280] hover:text-[#1A1A1A] hover:bg-white/60'
                              }
                              ${index === 0 ? 'rounded-l-lg' : ''}
                              ${index === QUARTERS.length - 1 ? 'rounded-r-lg' : ''}
                            `}
                            data-testid={`quarter-${quarter.value}`}
                          >
                            {quarter.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="bottom" 
                          sideOffset={8}
                          className="bg-[#1A1A1A] text-white border-0 px-3 py-2 z-50"
                        >
                          <p className="font-medium">{quarter.description}</p>
                          <p className="text-xs text-gray-400 mt-0.5">Forecast window: {quarter.range}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
                
                {/* Selected Quarter Info Badge */}
                <div className="hidden sm:flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1.5 rounded-md">
                  <Info className="w-3 h-3 mr-1.5" />
                  {QUARTERS.find(q => q.value === selectedQuarter)?.description}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-4 sm:p-6">
          {/* Intelligence Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Pipeline Growth */}
            <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200" data-testid="pipeline-growth-card">
              <p className="text-xs text-emerald-600 uppercase tracking-wider mb-2">Pipeline Growth</p>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-light text-emerald-700">
                  {summary.growth_percentage >= 0 ? '+' : ''}{summary.growth_percentage || 0}%
                </span>
                {summary.growth_percentage >= 0 ? (
                  <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500" />
                )}
              </div>
              <p className="text-xs text-emerald-600/70 mt-2">
                Over selected period
              </p>
            </div>

            {/* Projected Revenue */}
            <div className="p-4 rounded-lg bg-gradient-to-br from-[#B89B5E]/10 to-[#B89B5E]/5 border border-[#B89B5E]/30" data-testid="projected-revenue-card">
              <p className="text-xs text-[#B89B5E] uppercase tracking-wider mb-2">Projected Revenue (6mo)</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-light text-[#1A1A1A]">
                  {formatCurrency(summary.projected_6mo || 0)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Probability-weighted
              </p>
            </div>

            {/* Opportunities in Motion */}
            <div className="p-4 rounded-lg bg-muted/30 border border-[#E8E6E1]" data-testid="opps-in-motion-card">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">In Motion</p>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-light text-[#1A1A1A]">
                  {summary.opportunities_in_motion || 0}
                </span>
                <span className="text-sm text-muted-foreground">
                  / {summary.total_active_opportunities || 0}
                </span>
              </div>
              <p className="text-xs text-muted-foreground/70 mt-2">
                Active in last 30 days
              </p>
            </div>

            {/* Momentum Status */}
            <div className={`p-4 rounded-lg border ${getMomentumBg(summary.momentum_status)}`} data-testid="momentum-status-card">
              <p className="text-xs uppercase tracking-wider mb-2 opacity-70">Momentum Status</p>
              <div className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${getMomentumColor(summary.momentum_status)}`} />
                <span className={`text-lg font-medium ${getMomentumColor(summary.momentum_status)}`}>
                  {getMomentumLabel(summary.momentum_status)}
                </span>
              </div>
              <p className="text-xs opacity-60 mt-2">
                AI-evaluated health
              </p>
            </div>
          </div>

          {/* Chart Section */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Trend Summary Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Pipeline</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-light text-[#1A1A1A]">
                    {formatCurrency(summary.total_pipeline_value || 0)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-1">Active opportunities</p>
              </div>
              
              <div className="p-4 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Avg Deal Size</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-light text-[#1A1A1A]">
                    {formatCurrency(summary.average_deal_size || 0)}
                  </span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Added This Period</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-light text-emerald-600">
                    +{formatCurrency(summary.value_added_in_period || 0)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {summary.opps_added_in_period || 0} new opportunities
                </p>
              </div>
            </div>
            
            {/* Chart */}
            <div className="lg:col-span-3">
              <div className="relative">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart 
                    data={trendData}
                    onClick={handleChartClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <defs>
                      <linearGradient id="colorPipelineValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#B89B5E" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#B89B5E" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      hide 
                      domain={['dataMin - 50000', 'dataMax + 50000']} 
                    />
                    <RechartsTooltip 
                      formatter={(value) => [formatCurrency(value), 'Pipeline Value']}
                      contentStyle={{ 
                        backgroundColor: '#1A1A1A', 
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '12px'
                      }}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#B89B5E" 
                      strokeWidth={2}
                      fill="url(#colorPipelineValue)" 
                      activeDot={{ r: 6, fill: '#B89B5E', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                
                {/* Click Insight Panel */}
                {clickedPoint && (
                  <div className="absolute top-0 right-0 bg-white border border-[#E8E6E1] rounded-lg shadow-lg p-4 max-w-[200px] z-10">
                    <button 
                      onClick={() => setClickedPoint(null)}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-[#1A1A1A]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-muted-foreground mb-2">
                      {new Date(clickedPoint.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    </p>
                    <p className="text-lg font-light text-[#1A1A1A] mb-1">
                      {formatCurrency(clickedPoint.value)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {clickedPoint.count} opportunities
                    </p>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-2">
                Click any point to see details
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Generation Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <FileText className="w-5 h-5 text-[#B89B5E]" />
              Generate Pipeline Report
            </DialogTitle>
            <DialogDescription>
              Create a professional PDF report from your current pipeline data
            </DialogDescription>
          </DialogHeader>
          
          {!generatedReport ? (
            <>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">Select report type:</p>
                
                <div className="space-y-3">
                  <div
                    onClick={() => setSelectedReportType('executive')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedReportType === 'executive' 
                        ? 'border-[#B89B5E] bg-[#B89B5E]/5' 
                        : 'border-[#E8E6E1] hover:border-[#B89B5E]/50'
                    }`}
                    data-testid="report-type-executive"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        selectedReportType === 'executive' 
                          ? 'border-[#B89B5E] bg-[#B89B5E]' 
                          : 'border-muted-foreground'
                      }`}>
                        {selectedReportType === 'executive' && (
                          <div className="w-full h-full rounded-full bg-white scale-50" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#1A1A1A]">Executive Pipeline Intelligence Report</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Full metrics, forecasts, and strategic insights for internal review
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div
                    onClick={() => setSelectedReportType('client')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedReportType === 'client' 
                        ? 'border-[#B89B5E] bg-[#B89B5E]/5' 
                        : 'border-[#E8E6E1] hover:border-[#B89B5E]/50'
                    }`}
                    data-testid="report-type-client"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        selectedReportType === 'client' 
                          ? 'border-[#B89B5E] bg-[#B89B5E]' 
                          : 'border-muted-foreground'
                      }`}>
                        {selectedReportType === 'client' && (
                          <div className="w-full h-full rounded-full bg-white scale-50" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#1A1A1A]">Client Vision Progress Report</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Client-friendly format focusing on program progress and milestones
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setReportModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                  data-testid="generate-report-submit"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-lg font-medium text-[#1A1A1A] mb-1">Report Generated!</p>
                <p className="text-sm text-muted-foreground mb-4">
                  {generatedReport.filename}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => window.open(generatedReport.download_url, '_blank')}
                    data-testid="download-report-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    onClick={() => setSendEmailOpen(true)}
                    className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                    data-testid="send-report-email-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send via Email
                  </Button>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setGeneratedReport(null);
                    setReportModalOpen(false);
                  }}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailOpen} onOpenChange={setSendEmailOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#B89B5E]" />
              Send Report via Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                data-testid="report-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                placeholder="Add a personal message..."
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.to}
              className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
              data-testid="send-report-submit"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
